import { Link } from "react-router-dom"
import { Component, RefObject, createRef } from "react"

import { Spinner } from "components/Spinner"
import { TryAgainLater } from "components/TryAgainLater"
import { ImgAvatar, NonImgAvatar, VideoCallLocation } from "components"
import { Analytics } from "utils/analytics"
import { Button, Headline4 } from "ui-kit/components-branded/index"
import { BookingMove } from "modules/PsychologistAccount/BookingDetails/BookingMove"
import { AskUserModal } from "components/AskUserModal"
import {
    CancelBookingResponseType,
    Props,
    State,
    LoadBookingDetailsReasonType,
    CancelBookingReason,
} from "modules/PsychologistAccount/BookingDetails/BookingDetails/interfaces"
import {
    BOOKING_STATUSES_CANCELLED,
    STATUSES,
} from "modules/PsychologistAccount/BookingDetails/BookingDetails/constants"
import {
    BookingStatusBadge,
    refundIsImpossibleAlert,
} from "modules/PsychologistAccount/BookingDetails/BookingDetails/helpers"
import { getBookingDetails } from "modules/Payment/common/api"
import { isGetBookingDetailsResponse } from "modules/BookingCommon/common/utils"
import { GetBookingDetailsResponse } from "modules/BookingCommon/common/interfaces"
import { getFaceUrl, renderCityWithDetails } from "utils/helpers/psychologistUtil"
import { hasProp } from "utils/utils"
import * as utils from "utils/utils"
import { PSYALTER_SUPPORT_EMAIL } from "utils/env-management"
import { httpClient } from "utils/infrastructure/httpClient"

export class BookingDetails extends Component<Props, State> {
    refCancelButton: RefObject<TryAgainLater> = createRef()

    constructor(props: Props) {
        super(props)
        this.state = {
            status: STATUSES.loading,
            isEditing: false,
            refundIsPossible: true,
            details: undefined,
            pastSession: false,
            cancelledSession: false,
            canBeChanged: false,
            errorMessage: "",
            cancelErrorMessage: "",
            bookingHash: null,
        }
    }

    handleRefundCheck = (refundIsPossible: boolean) => {
        this.setState({ refundIsPossible })
    }

    componentDidMount() {
        this.setState({
            bookingHash: this.props.bookingHash,
        })
        this.props.bookingHash &&
            getBookingDetails(this.props.bookingHash).then(
                (data) => {
                    if (isGetBookingDetailsResponse(data)) {
                        this.setState({
                            status: STATUSES.loaded,
                            details: data,
                            pastSession: this.isPastSession(data),
                            cancelledSession: this.isSessionCancelled(data),
                            canBeChanged: this.isSessionCanBeChanged(data),
                        })
                    } else {
                        if (hasProp(data, "error")) {
                            throw new Error(data.error)
                        }
                        throw new Error("Invalid data from get-booking-details endpoint.")
                    }
                },
                (reason: LoadBookingDetailsReasonType) => {
                    this.setState({
                        status: STATUSES.failure,
                        errorMessage:
                            reason.error ||
                            "Что-то пошло не так. Попробуйте перезагрузить страницу через некоторое время",
                    })
                }
            )
        Analytics.booking_details_shown()
    }

    isPastSession(details: GetBookingDetailsResponse) {
        return new Date(details.rfc3339_start_datetime) <= new Date()
    }

    isSessionCancelled(details: GetBookingDetailsResponse) {
        return BOOKING_STATUSES_CANCELLED.includes(details.status)
    }

    isSessionCanBeChanged(details: GetBookingDetailsResponse) {
        return !this.isSessionCancelled(details) && !this.isPastSession(details)
    }

    cancelBooking = (bookingHash: string) =>
        httpClient
            .post<CancelBookingResponseType>("client-cancels-booking", {
                booking_hash: bookingHash,
            })
            .then((response: CancelBookingResponseType) => {
                if (response.result === "ok") {
                    Analytics.session_moved_without_refund()
                }
            })

    cancelBookingButtonText() {
        return this.state.refundIsPossible ||
            this.state.details?.is_free ||
            this.state.details?.price === 0
            ? "Отменить сессию"
            : "Отменить сессию без возврата денег"
    }

    onCancelBooking = (bookingHash: string) => {
        this.refCancelButton.current && this.refCancelButton.current.setLoading(true)
        this.setState({ cancelErrorMessage: undefined })
        bookingHash &&
            this.cancelBooking(bookingHash)
                .then(() => {
                    this.setState({ canBeChanged: false, cancelledSession: true })
                })
                .catch((reason: CancelBookingReason) => {
                    if (reason.error) {
                        this.setState({
                            canBeChanged: false,
                            cancelErrorMessage: reason.error,
                        })
                    } else {
                        this.setState({
                            canBeChanged: true,
                            cancelErrorMessage:
                                "Ошибка при выполнении операции. Попробуйте повторить действие позднее",
                        })
                    }
                })
                .finally(() => {
                    this.refCancelButton.current && this.refCancelButton.current.setLoading(false)
                })
    }

    render() {
        const {
            details,
            bookingHash,
            status,
            errorMessage,
            cancelErrorMessage,
            cancelledSession,
            canBeChanged,
            isEditing,
            refundIsPossible,
        } = this.state

        if (!details) return null

        const sessionStartDateTime =
            status === STATUSES.loaded && details.rfc3339_start_datetime
                ? utils.localDatetimeAsMoscow(new Date(details.rfc3339_start_datetime))
                : undefined

        const attributes = {
            href: details.psy_profile_url,
            target: "_blank",
            rel: "noopener noreferrer",
        }

        return (
            <div className="b-container">
                {status === STATUSES.loading && <Spinner />}
                {status === STATUSES.failure && (
                    <>
                        <div className="error-message alert-danger">{errorMessage}</div>
                        <TryAgainLater tryAgainFunction={() => window.location.reload()} />
                    </>
                )}
                {status === STATUSES.loaded && (
                    <>
                        <Headline4 className="has-text-centered">
                            Управление бронированиями
                        </Headline4>
                        <div className="booking-details-container">
                            <div className="booking-details-block">
                                <label>
                                    <b>Клиент</b>
                                </label>
                                <div></div>
                                <label>Имя:</label>
                                <div>{details.client_name}</div>
                                <label>E-mail:</label>
                                <div>{details.client_email}</div>
                                <label>Телефон:</label>
                                <div>{details.client_phone}</div>
                            </div>
                            <div className="booking-details-block">
                                <label />
                                <div className="psy-photo-container">
                                    <a {...attributes}>
                                        <ImgAvatar
                                            src={getFaceUrl(
                                                details.psy.face_url,
                                                details.psy.hash_id
                                            )}
                                            size="S"
                                            fallback={() => (
                                                <NonImgAvatar size="S">
                                                    {details.psy.display_name}
                                                </NonImgAvatar>
                                            )}
                                            className="small-photo"
                                            alt="Psychologist avatar"
                                        />
                                    </a>
                                </div>
                                <label>
                                    <b>Специалист</b>
                                </label>
                                <label>Имя:</label>
                                <a {...attributes}>{details.psy_full_name}</a>
                                <label>Телефон:</label>
                                <div>
                                    <a href={`tel:${details.psy_phone}`}>{details.psy_phone}</a>
                                </div>
                            </div>
                            <div className="booking-details-block">
                                <label>
                                    <b>Сессия</b>
                                </label>
                                <div></div>
                                <label>Дата:</label>
                                <div>
                                    {sessionStartDateTime &&
                                        utils.formatLocalDate(sessionStartDateTime)}
                                </div>
                                <label>Время:</label>
                                <div>
                                    {sessionStartDateTime &&
                                        utils.formatLocalTime(sessionStartDateTime)}{" "}
                                    {details.client_tz}
                                </div>
                                <label>Место:</label>
                                <div>
                                    {details.is_available_for_call ? (
                                        <Link to="/my/alter">
                                            Видеосвязь в&nbsp;личном кабинете Alter
                                        </Link>
                                    ) : details.is_online ? (
                                        <>
                                            <>💻 (онлайн)</>{" "}
                                            <VideoCallLocation address={details.address} />
                                        </>
                                    ) : (
                                        renderCityWithDetails(
                                            details.psy?.new_city?.name || "",
                                            details.psy.addresses,
                                            details.psy.city_details,
                                            <br />
                                        )
                                    )}
                                </div>
                                {details.price > 0 && this.props.showPrice && (
                                    <>
                                        <label>Стоимость:</label>
                                        <div>{details.price} ₽</div>
                                    </>
                                )}
                                <label>Статус:</label>
                                <div>
                                    <BookingStatusBadge status={details.status} />
                                </div>
                            </div>
                        </div>
                        {cancelledSession && (
                            <div className="status-message alert-warning">
                                Сессия отменена!
                                {details.price > 0 && (
                                    <>
                                        <br />
                                        Если вы&nbsp;отменили сессию ранее чем за&nbsp;24&nbsp;часа
                                        до&nbsp;ее&nbsp;начала, деньги вернутся на&nbsp;банковскую
                                        карту в&nbsp;течение 3-х рабочих дней.
                                    </>
                                )}
                            </div>
                        )}
                        {cancelErrorMessage && (
                            <div className="error-message alert-danger">{cancelErrorMessage}</div>
                        )}

                        {canBeChanged &&
                            (isEditing ? (
                                <>
                                    {!refundIsPossible && (
                                        <div className="info-message alert-danger mb-3">
                                            {details && refundIsImpossibleAlert(details)}
                                        </div>
                                    )}

                                    <AskUserModal
                                        refToButton={this.refCancelButton}
                                        modalClassName="ask-user-dialog"
                                        buttonName={this.cancelBookingButtonText()}
                                        onAccept={() =>
                                            bookingHash && this.onCancelBooking(bookingHash)
                                        }
                                    >
                                        Вы&nbsp;уверены, что хотите отменить сессию?
                                        <br />
                                    </AskUserModal>
                                    <br />
                                    <BookingMove
                                        booking={details}
                                        showPrice={this.props.showPrice}
                                        onRefundCheck={this.handleRefundCheck}
                                        cancelBooking={() =>
                                            bookingHash && this.cancelBooking(bookingHash)
                                        }
                                    />
                                </>
                            ) : (
                                <div className="cancel">
                                    <Button
                                        color="primary"
                                        onClick={() => this.setState({ isEditing: true })}
                                    >
                                        Изменить бронирование
                                    </Button>
                                </div>
                            ))}
                    </>
                )}
                <div className="extra-info">
                    <div className="info-message alert-light">
                        По&nbsp;всем вопросам связанным с&nbsp;бронированиями пишите на&nbsp;наш
                        адрес{" "}
                        <a href={`mailto:${PSYALTER_SUPPORT_EMAIL}`}>{PSYALTER_SUPPORT_EMAIL}</a>
                    </div>
                </div>
            </div>
        )
    }
}
